/**
 * THE TIME CLOCK'S READS AND THE DESK'S FIXES.
 *
 * Reads go through the same row level security as every other pay read:
 * a technician gets their own punches and the edits to them, the desk gets
 * the shop's. The fixes are four database functions from migration 0200
 * (clock_admin_edit, clock_admin_add, clock_admin_delete) that check the
 * caller is an admin or supervisor in the same shop, demand a reason, and
 * write the trail row themselves. Nothing here writes a table directly.
 *
 * BEFORE 0200 IS APPLIED pay_clock_edits does not exist. The trail read
 * then returns nothing, so the timesheet still draws, and a fix says the
 * feature is not switched on rather than putting a database error up.
 */

import { db } from "./client.ts";
import { addDays } from "./pay.ts";
import type { Punch, ClockEdit, BreakSpan } from "../domain/timeclock.ts";

export type SheetPunch = Punch & { hours_id: string | null };

export type SheetHours = {
  id: string; technician_id: string; worked_on: string;
  hours: number; rate: number | null; note: string | null; job_id: string | null;
};

const NOT_YET = "The time clock fixes are not switched on yet. Ask Anthony to apply them.";

/** Turn a missing function into a sentence a person can act on. */
function plain(message: string): string {
  if (/could not find the function|schema cache|does not exist/i.test(message)) return NOT_YET;
  return message;
}

type Row = Omit<SheetPunch, "breaks"> & { pay_clock_breaks: BreakSpan[] | null };
const unpack = (rows: Row[]): SheetPunch[] => rows.map(({ pay_clock_breaks, ...p }) =>
  ({ ...p, breaks: [...(pay_clock_breaks ?? [])].sort((a, b) => Date.parse(a.started_at) - Date.parse(b.started_at)) }));

/**
 * Punches that could fall in a week, padded a day each side because the
 * filter is UTC and the workday is Pacific. The caller trims by Pacific day.
 */
export async function punchesFor(from: string, to: string, techId?: string): Promise<SheetPunch[]> {
  const sb = await db();
  let q = sb.from("pay_clock")
    .select("id,technician_id,clock_in,clock_out,hours_id,pay_clock_breaks(started_at,ended_at)")
    .gte("clock_in", `${addDays(from, -1)}T00:00:00Z`)
    .lt("clock_in", `${addDays(to, 2)}T00:00:00Z`)
    .order("clock_in").limit(2000);
  if (techId) q = q.eq("technician_id", techId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return unpack((data ?? []) as unknown as Row[]);
}

/** The punch behind a pay_hours row, if the row came from the clock. */
export async function punchByHours(hoursId: string): Promise<SheetPunch | null> {
  const sb = await db();
  const { data, error } = await sb.from("pay_clock")
    .select("id,technician_id,clock_in,clock_out,hours_id,pay_clock_breaks(started_at,ended_at)")
    .eq("hours_id", hoursId).limit(1);
  if (error || !data?.length) return null;
  return unpack(data as unknown as Row[])[0] ?? null;
}

/** The pay_hours rows payroll will read for the week, clocked or typed. */
export async function hoursFor(from: string, to: string, techId?: string): Promise<SheetHours[]> {
  const sb = await db();
  let q = sb.from("pay_hours").select("id,technician_id,worked_on,hours,rate,note,job_id")
    .gte("worked_on", from).lte("worked_on", to).order("worked_on").limit(2000);
  if (techId) q = q.eq("technician_id", techId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as SheetHours[];
}

const EDIT_COLS = "id,technician_id,clock_id,action,editor_name,edited_at,before,after,reason";

/**
 * The trail for these shifts, plus any shift deleted from this week. Empty
 * before 0200, never an error.
 */
export async function editsFor(clockIds: string[], from: string, to: string, techId?: string): Promise<ClockEdit[]> {
  const sb = await db();
  const out: ClockEdit[] = [];
  if (clockIds.length) {
    const { data, error } = await sb.from("pay_clock_edits").select(EDIT_COLS)
      .in("clock_id", clockIds).order("edited_at");
    if (error) return [];
    out.push(...((data ?? []) as unknown as ClockEdit[]));
  }
  let q = sb.from("pay_clock_edits").select(EDIT_COLS).eq("action", "delete")
    .gte("before->>worked_on", from).lte("before->>worked_on", to).order("edited_at");
  if (techId) q = q.eq("technician_id", techId);
  const { data, error } = await q;
  if (!error) out.push(...((data ?? []) as unknown as ClockEdit[]));
  return out;
}

/** A shift's new times. Breaks are the whole list and replace what was there. */
export type ShiftTimes = { clock_in: string; clock_out: string | null; breaks: BreakSpan[] };

export type FixResult = { clock_id: string; action: string; flags?: string[] };

export async function editShift(clockId: string, t: ShiftTimes, reason: string): Promise<FixResult> {
  if (reason.trim().length < 3) throw new Error("Say why. Every change to the clock keeps a reason.");
  const sb = await db();
  const { data, error } = await sb.rpc("clock_admin_edit", {
    p_clock_id: clockId, p_clock_in: t.clock_in, p_clock_out: t.clock_out,
    p_breaks: t.breaks, p_reason: reason.trim(),
  });
  if (error) throw new Error(plain(error.message));
  return data as unknown as FixResult;
}

export async function addShift(techId: string, t: ShiftTimes, reason: string): Promise<FixResult> {
  if (reason.trim().length < 3) throw new Error("Say why. Every change to the clock keeps a reason.");
  const sb = await db();
  const { data, error } = await sb.rpc("clock_admin_add", {
    p_technician_id: techId, p_clock_in: t.clock_in, p_clock_out: t.clock_out,
    p_breaks: t.breaks, p_reason: reason.trim(),
  });
  if (error) throw new Error(plain(error.message));
  return data as unknown as FixResult;
}

export async function deleteShift(clockId: string, reason: string): Promise<void> {
  if (reason.trim().length < 3) throw new Error("Say why. Every change to the clock keeps a reason.");
  const sb = await db();
  const { error } = await sb.rpc("clock_admin_delete", { p_clock_id: clockId, p_reason: reason.trim() });
  if (error) throw new Error(plain(error.message));
}

/** Hourly people, for the desk's person filter and the missed-shift form. */
export async function hourlyIds(): Promise<Set<string>> {
  const sb = await db();
  const { data } = await sb.from("pay_terms").select("technician_id,basis").eq("basis", "hourly");
  return new Set(((data ?? []) as Array<{ technician_id: string }>).map((r) => r.technician_id));
}
